import CONFIG from "./../config.json";
import { cacheGetCacheTime, IfQuickReloadFlushEverything } from "./cache.js";
import { posterCreateAndAppend, posterRender, posterAppendAuthor, posterParse } from "./poster.js";
import {
    postersGetPosterResposiveWidth,
    postersSetExcludedPosterIds,
    postersSetIndexMode,
    postersUnescapeSpacesAndApostrophes
} from "./posters.js";
import { controlStartUp } from "./control.js";
import { empGetEditorUrl } from "./emp.js";
import { ADSENSE_MAXIMUM_WIDTH, adsenseAddAndPush, adsensePushContainerAds } from "./adsense.js";
import {
    pageFetchPosters,
    pageGetNextPageHref,
    pageIsThereMorePosters
} from "./page.js";
import { loadMoreBind } from "./load-more.js";

const DATE_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/;
const WEEKEND_STORAGE_PREFIX = "weekendPosters:";
let weekendCursor = null;
let weekendPendingPosters = [];
let weekendFinished = false;

function weekendParseDate(value) {
    const match = DATE_PATTERN.exec(value || "");
    if (!match) {
        return null;
    }
    return new Date(parseInt(match[3], 10), parseInt(match[2], 10) - 1, parseInt(match[1], 10));
}

function weekendDateToApiPath(date) {
    return date.getDate() + "/" + (date.getMonth() + 1);
}

function weekendMatchesPlace(poster, vegueria, town) {
    if (vegueria === "") {
        return town === "" || poster.town === town;
    }
    const regions = Array.isArray(window.weekendRegions) ? window.weekendRegions : [];
    if (!regions.includes(poster.region)) {
        return false;
    }
    return town === "" || poster.town === town;
}

function weekendFetchBatch(cursor = null) {
    const today = new Date();
    let resource = `${CONFIG.posters_api_url}/resource/poster/public/from/${weekendDateToApiPath(today)}`;
    let separator = "?";
    if (cursor !== null && cursor !== "") {
        resource += separator + "cursor=" + encodeURIComponent(cursor);
        separator = "&";
    }
    const cacheTime = cacheGetCacheTime();
    if (cacheTime !== null && cacheTime !== "") {
        resource += separator + "cacheTime=" + cacheTime;
    }

    return fetch(resource, {headers: {"X-Cartells-Frontend-Client": "EMP_POSTERS"}})
            .then((response) => response.json());
}

async function weekendCollectPosters(limit, vegueria, town, friday, sunday, excludedIds) {
    const posters = [];
    const excluded = new Set(excludedIds);
    const thursday = new Date(friday);
    thursday.setDate(thursday.getDate() - 1);
    const tuesday = new Date(sunday);
    tuesday.setDate(tuesday.getDate() + 2);

    while (posters.length < limit && !weekendFinished) {
        if (weekendPendingPosters.length === 0) {
            const payload = await weekendFetchBatch(weekendCursor);
            weekendPendingPosters = Array.isArray(payload.list) ? payload.list.slice() : [];
            weekendCursor = payload.cursor || "";
            if (weekendPendingPosters.length === 0 && weekendCursor === "") {
                weekendFinished = true;
                break;
            }
        }

        while (weekendPendingPosters.length > 0 && posters.length < limit) {
            const poster = weekendPendingPosters.shift();
            const endDate = weekendParseDate(poster.dateEnd);
            if (!endDate) {
                continue;
            }
            if (endDate > tuesday) {
                weekendPendingPosters = [];
                weekendFinished = true;
                break;
            }
            const beginDate = weekendParseDate(poster.dateBegin);
            // Keep the same Thursday-to-Tuesday selection as WeekendPosterService.
            if (!beginDate || beginDate < thursday || beginDate > sunday
                    || endDate < friday || endDate < beginDate
                    || poster.draft === true || excluded.has(poster.id)) {
                continue;
            }
            if (weekendMatchesPlace(poster, vegueria, town)) {
                posters.push(poster);
                excluded.add(poster.id);
            }
        }

        if (weekendPendingPosters.length === 0 && weekendCursor === "") {
            weekendFinished = true;
        }
    }

    return posters;
}

function weekendBindMainLoadMore(wrapper, vegueria, town, friday, sunday) {
    const buttonWrapper = document.getElementById("mainNextPageButtonWrapper");
    const button = document.getElementById("mainNextPageButton");
    if (!buttonWrapper || !button) {
        return;
    }

    const renderedPosters = weekendGetRenderedPosterIds(wrapper).length;
    buttonWrapper.style.display = !weekendFinished && renderedPosters >= 20
            ? "block"
            : "none";
    loadMoreBind(button, async () => {
        const posters = await weekendCollectPosters(
                20,
                vegueria,
                town,
                friday,
                sunday,
                weekendGetRenderedPosterIds(wrapper)
                );
        weekendAppendPosterList(posters, wrapper);
        if (posters.length === 0 || weekendFinished) {
            buttonWrapper.style.display = "none";
        }
    });
}

function weekendGetStorageKey(vegueria, town, friday, sunday) {
    const cacheTime = cacheGetCacheTime() || "default";
    return [
        WEEKEND_STORAGE_PREFIX,
        "thursday-tuesday-v1",
        cacheTime,
        vegueria,
        town,
        friday,
        sunday
    ].join(":");
}

function weekendReadCachedPosters(storageKey) {
    try {
        const cached = sessionStorage.getItem(storageKey);
        if (!cached) {
            return null;
        }
        const parsed = JSON.parse(cached);
        return Array.isArray(parsed) ? parsed : null;
    } catch (err) {
        return null;
    }
}

function weekendWriteCachedPosters(storageKey, posters) {
    try {
        sessionStorage.setItem(storageKey, JSON.stringify(posters));
    } catch (err) {
        // Ignore storage failures.
    }
}

function weekendClearStaleCachedPosters(activeKey) {
    try {
        for (let i = sessionStorage.length - 1; i >= 0; i--) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith(WEEKEND_STORAGE_PREFIX) && key !== activeKey) {
                sessionStorage.removeItem(key);
            }
        }
    } catch (err) {
        // Ignore storage failures.
    }
}

function weekendShowEmptyMessage(wrapper, town, vegueria) {
    const element = document.createElement("div");
    element.setAttribute("id", "mainNoPostersMessage");
    const place = town !== ""
            ? town
            : vegueria || (document.documentElement.lang === "es"
                    ? "Cataluña y Andorra"
                    : "Catalunya i Andorra");
    element.innerHTML =
            "No hi ha cartells aquest cap de setmana a <b class='nowrap'>" +
            place +
            "</b>, publica el teu cartell a <a class='nowrap' href='" + empGetEditorUrl() + "'>editor.yuppiya.com</a>";
    wrapper.appendChild(element);
}

function weekendAppendPosterList(posters, wrapper) {
    let adsNumber = 0;
    posters.forEach((poster, index) => {
        posterCreateAndAppend(poster, wrapper);
        const posterNumber = index + 1;
        if (Math.floor(posterNumber / 3) > adsNumber) {
            adsNumber++;
            adsenseAddAndPush(
                    wrapper,
                    postersGetPosterResposiveWidth(ADSENSE_MAXIMUM_WIDTH)
                    );
        }
    });
}

function weekendEnsureListingAd(wrapper) {
    if (wrapper.getElementsByClassName("adWrapper").length > 0) {
        return;
    }
    const fallback = document.querySelector(".mainAdWrapper");
    if (fallback) {
        adsenseAddAndPush(fallback);
    }
}

function weekendGetRenderedPosterIds(wrapper) {
    return Array.from(wrapper.getElementsByClassName("posterWrapper"))
            .map((poster) => poster.getAttribute("data-post-id"))
            .filter((id) => id !== null && id !== "");
}

function weekendHideOtherPosters() {
    const section = document.getElementById("mainOtherPosters");
    if (section) {
        section.style.display = "none";
    }
}

function weekendShowOtherPosters(excludedIds) {
    const section = document.getElementById("mainOtherPosters");
    const wrapper = document.getElementById("mainOtherPostersWrapper");
    if (!section || !wrapper) {
        return Promise.resolve();
    }

    if (wrapper.dataset.postersLoaded === "true") {
        const hasPosters = wrapper.getElementsByClassName("posterWrapper").length > 0;
        section.style.display = hasPosters ? "block" : "none";
        posterRender(wrapper);
        Array.from(wrapper.getElementsByClassName("posterWrapper")).forEach((poster) => {
            posterAppendAuthor(poster);
            posterParse(poster);
        });
        return Promise.resolve();
    }

    const regions = Array.isArray(window.weekendRegions) ? window.weekendRegions : [];
    const hasPlaceFilter = regions.length > 0;
    const regionFilter = hasPlaceFilter ? regions : "";

    const remainingSlots = Math.max(0, 20 - excludedIds.length);
    if (remainingSlots <= 0) {
        weekendHideOtherPosters();
        return Promise.resolve();
    }
    const targetPostersNumber = remainingSlots;

    section.style.display = "block";
    controlStartUp("mainOtherSentinel");
    postersSetExcludedPosterIds(excludedIds);
    postersSetIndexMode("others");

    const params = new URL(document.location).searchParams;
    let otherIndex = parseInt(params.get("otherIndex"), 10);
    if (Number.isNaN(otherIndex)) {
        otherIndex = 0;
    }
    const otherCursor = params.get("otherCursor") || "";

    return pageFetchPosters(
            wrapper,
            regionFilter,
            "",
            "",
            otherIndex,
            otherCursor,
            null,
            {
                forceFullPage: true,
                targetPostersNumber
            }
            ).then(() => {
        const nextWrapper = document.getElementById("mainOtherNextPageButtonWrapper");
        const nextButton = document.getElementById("mainOtherNextPageButton");
        if (!nextWrapper || !nextButton) {
            return;
        }
        if (pageIsThereMorePosters()) {
            nextWrapper.style.display = "block";
            nextButton.setAttribute("href", pageGetNextPageHref("otherIndex", true, "otherCursor"));
            loadMoreBind(nextButton, async () => {
                const href = nextButton.getAttribute("href");
                if (!href) {
                    return;
                }
                const nextUrl = new URL(href, window.location.origin);
                const nextIndex = parseInt(nextUrl.searchParams.get("otherIndex"), 10);
                const nextCursor = nextUrl.searchParams.get("otherCursor") || "";
                postersSetExcludedPosterIds(excludedIds);
                postersSetIndexMode("others");
                try {
                    await pageFetchPosters(
                            wrapper,
                            regionFilter,
                            "",
                            "",
                            Number.isNaN(nextIndex) ? 0 : nextIndex,
                            nextCursor
                            );
                    if (pageIsThereMorePosters()) {
                        nextButton.setAttribute("href", pageGetNextPageHref("otherIndex", true, "otherCursor"));
                    } else {
                        nextWrapper.style.display = "none";
                        nextButton.removeAttribute("href");
                    }
                } finally {
                    postersSetExcludedPosterIds([]);
                }
            });
        } else {
            nextWrapper.style.display = "none";
            nextButton.removeAttribute("href");
        }
        adsensePushContainerAds(section);
    }).finally(() => {
        wrapper.dataset.postersLoaded = "true";
        postersSetExcludedPosterIds([]);
    });
}

async function weekendStartUp(vegueriaValue, townValue) {
    const wrapper = document.getElementById("mainPostersWrapper");
    const loader = document.getElementById("mainLoader");
    if (!wrapper || wrapper.dataset.postersLoaded === "true") {
        posterRender(wrapper);
        Array.from(wrapper.getElementsByClassName("posterWrapper")).forEach((poster) => {
            posterAppendAuthor(poster);
            posterParse(poster);
        });
        weekendEnsureListingAd(wrapper);
        weekendShowOtherPosters(weekendGetRenderedPosterIds(wrapper));
        return;
    }

    const vegueria = postersUnescapeSpacesAndApostrophes(vegueriaValue || "");
    const town = postersUnescapeSpacesAndApostrophes(townValue || "");
    const friday = weekendParseDate(document.getElementById("mainBody")?.dataset.weekendFriday);
    const sunday = weekendParseDate(document.getElementById("mainBody")?.dataset.weekendSunday);
    if (!friday || !sunday) {
        return;
    }

    if (loader) {
        loader.style.display = "inline-block";
    }

    if (await IfQuickReloadFlushEverything()) {
        return;
    }

    const fridayValue = document.getElementById("mainBody")?.dataset.weekendFriday || "";
    const sundayValue = document.getElementById("mainBody")?.dataset.weekendSunday || "";
    const storageKey = weekendGetStorageKey(vegueria, town, fridayValue, sundayValue);
    weekendClearStaleCachedPosters(storageKey);

    const cachedPosters = weekendReadCachedPosters(storageKey);
    if (cachedPosters !== null) {
        weekendAppendPosterList(cachedPosters, wrapper);
        weekendEnsureListingAd(wrapper);
        if (cachedPosters.length === 0) {
            weekendShowEmptyMessage(wrapper, town, vegueria);
        }
        wrapper.dataset.postersLoaded = "true";
        if (loader) {
            loader.style.display = "none";
        }
        weekendBindMainLoadMore(wrapper, vegueria, town, friday, sunday);
        await weekendShowOtherPosters(cachedPosters.map((poster) => poster.id).filter(Boolean));
        return;
    }

    const weekendPosters = await weekendCollectPosters(20, vegueria, town, friday, sunday, []);

    weekendWriteCachedPosters(storageKey, weekendPosters);
    weekendAppendPosterList(weekendPosters, wrapper);
    weekendEnsureListingAd(wrapper);

    if (weekendPosters.length === 0) {
        weekendShowEmptyMessage(wrapper, town, vegueria);
    }
    wrapper.dataset.postersLoaded = "true";
    if (loader) {
        loader.style.display = "none";
    }
    weekendBindMainLoadMore(wrapper, vegueria, town, friday, sunday);
    await weekendShowOtherPosters(weekendPosters.map((poster) => poster.id).filter(Boolean));
}

export { weekendStartUp };
